<template>
  <form @submit.prevent="submit">
    <div class="grid grid-cols-1 gap-4 md:gap-6 mt-4 lg:mt-8 lg:grid-cols-2">
      <div class="col-span-1 md:col-span-2">
        <div class="text-xl font-bold text-center">
          {{ __('vip-order-for-who.form.title') }}
        </div>
      </div>
      <div class="w-full">
        <InputField
          v-model="form.for"
          :form="form"
          classes="border"
          name="for"
          type="text"
          :label="__('vip-order-for-who.form.for.text')"
          :placeholder="__('vip-order-for-who.form.for.placeholder')"
          maxlength="255"
        />

        <div class="mt-1">
          <InputField
            v-model="form.name"
            :form="form"
            classes="mt-3 border"
            name="name"
            type="text"
            :label="__('vip-order-who-are-you.form.name.text')"
            :placeholder="__('vip-order-who-are-you.form.name.placeholder')"
            maxlength="255"
          />
        </div>

        <div class="mt-1">
          <InputField
            v-model="form.email"
            :form="form"
            classes="mt-3 border"
            name="email"
            type="email"
            :label="__('vip-order-who-are-you.form.email.text')"
            :placeholder="__('vip-order-who-are-you.form.email.placeholder')"
            maxlength="255"
          />
        </div>

        <div class="mt-1">
          <InputField
            v-model="form.phone"
            :form="form"
            classes="mt-3 border"
            name="phone"
            type="tel"
            :label="__('vip-order-who-are-you.form.phone.text')"
            :placeholder="__('vip-order-who-are-you.form.phone.placeholder')"
            :is-required="false"
            maxlength="255"
          >
            <template #label>
              <i18n-t
                keypath="vip-order-who-are-you.form.phone.extra.text"
                scope="global"
              >
                <template #extra>
                  <span class="font-normal text-xs">{{ __('vip-order-who-are-you.form.phone.extra.subtext') }}</span>
                </template>
              </i18n-t>
            </template>
          </InputField>
        </div>
      </div>

      <div class="w-full">
        <div class="flex justify-between items-center">
          <div>
            <span class="text-sm font-bold dark:text-white">
              {{ __('vip-order-for-who.form.message.text', {vip: vip.first_name}) }}
            </span>

            <span class="text-red">
              *
            </span>
          </div>

          <div>
            <button
              v-if="ai && !generating"
              type="button"
              class="button-blue !py-[0.125rem] px-1 text-sm"
              tabindex="-1"
              @click.stop="generateText"
            >
              {{ __('vip-order-who-are-you.generate') }}
            </button>
            <div
              v-if="ai"
              class="relative inline-block has-tooltip w-[15px] h-[15px] ml-1"
            >
              <span class="tooltip relative right-5 z-10 rounded shadow-lg p-3 bg-white w-[200px] font-normal">
                {{ __('vip-order-for-who.form.message.tooltip') }}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
              >
                <path
                  d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24V250.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1H222.6c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        <TextareaField
          v-model="form.message"
          :form="form"
          classes="border pb-5 min-h-[10rem]"
          label=""
          :max-length="300"
          :placeholder="selectedOccasion.placeholder"
          :show-is-required="false"
          name="message"
          rows="5"
          style="field-sizing:content"
        />

        <div v-if="generating">
          <svg
            class="w-3 inline-block mr-1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152V424c0 48.6 39.4 88 88 88H360c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24V424c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40H200c13.3 0 24-10.7 24-24s-10.7-24-24-24H88z" />
          </svg>
          Onze tekstschrijver is hard aan het werk!
        </div>
      </div>
    </div>

    <div class="mt-6">
      <div class="relative flex items-start">
        <div class="flex items-center h-6">
          <input
            id="terms"
            v-model="form.conditions"
            type="checkbox"
            class="focus:ring-red h-4 w-4 text-red border-gray-200 rounded"
            name="terms"
            required
          >
        </div>
        <div class="ml-2">
          <label
            for="terms"
            class=" text-xs"
          >
            <i18n-t
              keypath="vip-order-who-are-you.form.terms"
              scope="global"
            >
              <template #link>
                <a
                  :href="localeRoute('frontend.terms-and-conditions')"
                  class="underline hover:no-underline"
                  target="_blank"
                >
                  {{ __('vip-order-who-are-you.form.terms-link') }}
                </a>
              </template>
            </i18n-t>
          </label>
        </div>
      </div>

      <div class="relative flex items-start lg:mt-1">
        <div class="flex items-center h-6">
          <input
            id="share_video"
            v-model="form.do_not_share_video"
            type="checkbox"
            class="focus:ring-red h-4 w-4 text-red border-gray-200 rounded"
          >
        </div>
        <div class="ml-2">
          <!-- eslint-disable vue/no-v-html -->
          <label
            for="share_video"
            class="text-xs"
            v-html="__('vip-order-who-are-you.form.make-private-video')"
          />
        </div>
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <div class="relative flex items-start lg:mt-1">
        <div class="flex items-center h-6">
          <input
            id="no_logo"
            v-model="form.no_logo"
            type="checkbox"
            class="focus:ring-red h-4 w-4 text-red border-gray-200 rounded"
          >
        </div>
        <div class="ml-2">
          <!-- eslint-disable vue/no-v-html -->
          <label
            for="no_logo"
            class="text-xs"
            v-html="__('vip-order-who-are-you.form.no-logo')"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>

      <div class="mt-12 lg:mt-8 w-full lg:text-center">
        <button
          type="submit"
          class="button-green shadow-button-black w-full block lg:w-auto lg:inline-block lg:px-16"
        >
          {{ __('vip-order-who-are-you.continue') }}
          <img
            src="/frontend/assets/white-arrow.svg"
            class="inline ml-2"
          >
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import axios from 'axios';
import InputField from '../Form/InputField.vue';
import TextareaField from '../Form/TextareaField.vue';
import GoogleTagManager from '../../Mixins/GoogleTagManager';

export default {
  components: {
    InputField,
    TextareaField,
  },

  mixins: [GoogleTagManager],

  props: {
    vip: {
      type: Object,
      required: true,
    },

    occasions: {
      type: Array,
      required: true,
    },

    ai: {
      type: Boolean,
      required: true,
    },
  },

  setup (props) {
    const form = useForm({
      occasion_slug: props.occasions[0].slug,
      for: '',
      message: '',
      name: '',
      email: '',
      phone: '',
      how_do_you_know: '',
      conditions: false,
      do_not_share_video: false,
      no_logo: false,
    });

    return {
      form,
    };

  },

  data() {
    return {
      generating: false,
    };
  },

  computed: {
    selectedOccasion() {
      return this.occasions.find((occasion) => occasion.slug === this.form.occasion_slug);
    },
  },

  methods: {
    occasionClasses(index) {
      const classes = [];
      classes.push('ml-2 pl-6 pr-6 lg:pr-4');

      if (this.form.occasion_slug === this.occasions[index].slug) {
        classes.push('border border-red bg-red text-white');
      } else {
        classes.push('border-2 border-black text-black');
      }

      return classes;
    },

    generateText() {
      this.generating = true;
      axios.post(this.localeRoute('frontend.api.generate-text'), this.form)
        .then(({data}) => {
          this.generating = false;
          this.form.message = data.message;
        });
    },

    submit() {
      this.gtmAddToCart(this.vip);

      this.form.post(this.localeRoute('frontend.vips.order.create', [this.vip]));
    },
  },
};
</script>

